import React from 'react';
import { styled } from 'linaria/react';

import { useTracker } from '@jetshop/core/analytics/Analytics';
import { trackCartCheckoutEvent } from '@jetshop/core/analytics/tracking';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import CartProvider from '@jetshop/core/components/Query/CartProvider';
import t from '@jetshop/intl';
import Image from '@jetshop/ui/Image/Image';
import { Price } from '@jetshop/ui/Price';
import useProductToast from './useProductToast';
import cartQuery from '../../../Cart/queries/cartQuery.gql';
import { theme } from '../../../Theme';

const Container = styled('aside')`
  padding: 20px;
`;

const CheckoutButton = styled('a')`
  width: 100%;
  height: 45px;
  color: white;
  background: ${theme.colors.accent};
  font-size: 12px;
  font-weight: bold;
  text-align: center;
  line-height: 45px;
  text-decoration: none;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  letter-spacing: 0.2em;
  text-transform: uppercase;

  &:hover {
    background: ${theme.colors.variant.accent};
  }

  .loader {
    height: 12px;
    width: 60px;
  }
  .loader-dot {
    height: 6px !important;
    width: 6px !important;
    padding: 0 !important;
  }
`;

const ProductImageWrapper = styled('div')`
  width: 75px;
  position: relative;
  &:after {
    content: '';
    background: rgba(0, 0, 0, 0.04);
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
`;

const ProductCheckoutContainer = styled('div')``;

const Product = styled('div')`
  display: flex;
  align-items: center;
  padding: 10px 0;
`;

const ProductDetails = styled('div')`
  padding: 0 20px;
  .product-price {
    font-size: 16px;
    font-weight: bold;
    line-height: 1;
  }
  .product-name {
    font-size: 16px;
    margin: 0;
  }
`;

const Header = styled('h3')``;

const ProductName = styled('p')`
  max-width: 200px;
`;

const Error = styled('p')``;

const ProductToast = ({
  product,
  cart,
  selectedVariation,
  quantity,
  error
}) => {
  const { price, previousPrice, image } = useProductToast({
    product,
    selectedVariation,
    quantity
  });
  const track = useTracker();
  const hasImages = product.images && product.images.length > 0;
  const noImage =
    'https://willabb2c-eclipse.jetshop.se/pub_docs/files/missingImage.png';
  return (
    <Container data-testid="product-toast">
      <ProductCheckoutContainer>
        <Product>
          {hasImages ? (
            <ProductImageWrapper>
              <Image
                src={image.url}
                sizes={80}
                aspect={'1:1'}
                alt={image.alt}
                quality={80}
                modifiedDate={image.modifiedDate}
              />
            </ProductImageWrapper>
          ) : (
            <ProductImageWrapper>
              <Image
                className="no-image"
                sizes={80}
                aspect={'1:1'}
                src={noImage}
                alt="noimage"
              />
            </ProductImageWrapper>
          )}
          <ProductDetails>
            {error ? (
              <Header>{t('Failed adding to bag')}</Header>
            ) : quantity === 1 ? (
              <Header>{t('Added to bag')}</Header>
            ) : (
              <Header>
                {t.rich('Added {quantity} items to bag', { quantity })}
              </Header>
            )}
            <ProductName>{product.name}</ProductName>
            {/* {!error && (
              <Price
                price={price?.price?.exVat}
                previousPrice={previousPrice}
              />
            )} */}
            {error && (
              <Error>
                {t('An error occurred. Details:')}
                <ul>
                  {error.graphQLErrors && error.graphQLErrors.length > 0 ? (
                    error?.graphQLErrors?.map(
                      ({ message, locations, path }) => (
                        <li key={message}>{t(message)}</li>
                      )
                    )
                  ) : (
                    <li>{t(error.message)}</li>
                  )}
                </ul>
              </Error>
            )}
          </ProductDetails>
        </Product>
        {cart && cart.externalCheckoutUrl && (
          <ChannelContext.Consumer>
            {({ selectedChannel }) => (
              <CheckoutButton
                href={cart.externalCheckoutUrl}
                onClick={event => {
                  event.preventDefault();
                  track(
                    trackCartCheckoutEvent({
                      cart: cart,
                      callback: () => {
                        window.location = cart.externalCheckoutUrl;
                      }
                    })
                  );
                }}
              >
                {t('To checkout')}
              </CheckoutButton>
            )}
          </ChannelContext.Consumer>
        )}
      </ProductCheckoutContainer>
    </Container>
  );
};
const ProductToastWrapper = props => (
  <CartProvider query={cartQuery}>
    {({ data }) => <ProductToast {...props} cart={data && data.cart} />}
  </CartProvider>
);

export default ProductToastWrapper;
